import React from 'react';
import { Link } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { useAnonymization } from '../utils/anonymize';
import { formatUTCDate } from '../utils/dateUtils';
import { cn } from '../utils/cn';

interface Contact {
  id: string;
  first_name: string;
  last_name: string;
  work_email?: string;
  personal_email?: string;
  company?: {
    id: string;
    name: string;
  };
}

interface ContactGridProps {
  contacts: Array<{
    id: string;
    contact: Contact;
    due_date?: string;
  }>;
  shouldAnonymize?: boolean;
  showFollowUp?: boolean;
}

export default function ContactGrid({ 
  contacts, 
  shouldAnonymize = false,
  showFollowUp = true
}: ContactGridProps) {
  return (
    <div className="bg-dark-800 rounded-lg overflow-hidden">
      {/* Table Headers */}
      <div className="grid grid-cols-10 gap-4 px-4 py-3 border-b border-dark-700">
        <div className="col-span-4 text-xs font-medium text-gray-400 uppercase">Name</div>
        <div className={cn(
          "text-xs font-medium text-gray-400 uppercase",
          showFollowUp ? "col-span-4" : "col-span-6"
        )}>Email</div>
        {showFollowUp && (
          <div className="col-span-2 text-xs font-medium text-gray-400 uppercase">Next Follow Up</div>
        )}
      </div>

      {/* Contact List */}
      <div className="divide-y divide-dark-700">
        {contacts
          .sort((a, b) => new Date(a.due_date || '').getTime() - new Date(b.due_date || '').getTime())
          .map(item => (
            <Link 
              key={item.id}
              to={`/contacts/${item.contact.id}`}
              className="block hover:bg-dark-700"
            >
              <div className="grid grid-cols-10 gap-4 px-4 py-3">
                <div className="col-span-4 text-sm text-white">
                  {useAnonymization(item.contact.first_name, 'name', shouldAnonymize)}{' '}
                  {useAnonymization(item.contact.last_name, 'name', shouldAnonymize)}
                </div>
                <div className={cn(
                  "text-sm text-gray-400 flex flex-col gap-1",
                  showFollowUp ? "col-span-4" : "col-span-6"
                )}>
                  {item.contact.work_email && (
                    <span className="inline-flex items-center gap-2">
                      <Mail className="w-4 h-4 text-gray-500" />
                      {item.contact.work_email}
                    </span>
                  )}
                  {item.contact.personal_email && (
                    <span className="inline-flex items-center gap-2">
                      <Mail className="w-4 h-4 text-gray-500" />
                      {item.contact.personal_email}
                    </span>
                  )}
                </div>
                {showFollowUp && (
                  <div className="col-span-2 text-sm text-gray-400">
                    {item.due_date && formatUTCDate(item.due_date)}
                  </div>
                )}
              </div>
            </Link>
          ))}
      </div>
    </div>
  );
} 